// The authenticated + reseller boundary (rules 12, 13): one session check, one reseller lookup,
// for every page below. The API decides; this only routes on its answer. The person signed in
// (for the account menu) is read after the reseller, so a 401 is the API's, not the session's.
import { createFileRoute, Outlet, redirect, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { AuthCard } from "@/components/auth-card";
import { currentUserQuery } from "@/features/auth/queries";
import { SignOutButton } from "@/features/auth/sign-out/button";
import { useSignOut } from "@/features/auth/sign-out/mutation";
import { currentResellerQuery } from "@/features/reseller/queries";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    try {
      const reseller = await context.queryClient.ensureQueryData(currentResellerQuery());
      const user = await context.queryClient.ensureQueryData(currentUserQuery());
      return { reseller, user };
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        throw redirect({ to: "/login", search: { redirect: location.href } });
      }
      throw error;
    }
  },
  component: AuthenticatedLayout,
  errorComponent: BoundaryError,
});

function AuthenticatedLayout() {
  const { reseller, user } = Route.useRouteContext();
  const router = useRouter();
  const signOut = useSignOut();
  return (
    <AppShell
      resellerName={reseller.name}
      user={user}
      onSignOut={() => signOut(() => router.navigate({ to: "/login" }))}
    >
      <Outlet />
    </AppShell>
  );
}

// A rejected application is a 403 from the portal chain: say so, and let them leave.
function BoundaryError({ error }: ErrorComponentProps) {
  const router = useRouter();
  const rejected = error instanceof ApiError && error.status === 403;
  return (
    <AuthCard
      title={rejected ? m.portal_rejected_title() : m.common_error_title()}
      description={rejected ? m.portal_rejected_description() : m.common_error_description()}
    >
      <SignOutButton onSignedOut={() => router.navigate({ to: "/login" })} />
    </AuthCard>
  );
}
